import { FC, useMemo } from 'react';

import { ColumnTable, Entry } from '@/components/ColumnTable';
import { useTest } from '@/hooks/dbtStrategy/useTest';
import { useCommonLineageEntries } from '@/hooks/useCommonLineageEntries';
import { toTitleCase } from '@/util/string';
import { PanelDetails, ResourceDetailsParams } from './panelDetailTypes';
import { TagLinks } from './TagLinks';

export const useTestDetailsPanel = ({
  uniqueId,
  projectName,
}: ResourceDetailsParams): PanelDetails => {
  const testQuery = useTest(uniqueId);

  const commonEntries = useCommonLineageEntries(projectName);

  return useMemo((): PanelDetails => {
    const test = testQuery.data.applied;

    if (!test) {
      return { ...testQuery, rows: null, description: null };
    }

    const unsortedEntries: Entry[] = [
      {
        key: 'Column Name',
        data: test.columnName,
      },
      {
        key: 'Tags',
        data: <TagLinks tags={test.tags} />,
      },
      {
        key: 'Package',
        data: uniqueId.packageName,
      },
    ];

    if (test.testMetadata?.name === 'accepted_values') {
      const acceptedValues = test.testMetadata?.kwargs?.values;
      if (acceptedValues?.length) {
        unsortedEntries.push({
          key: 'Accepted Values',
          data: acceptedValues.join(', '),
        });
      }
    }

    Object.entries((test.config as Record<string, any> | null) ?? {}).forEach(
      ([key, data]) => {
        const newKey = toTitleCase(key.split('_').join(' '));
        const sanitizedData =
          typeof data === 'object' ? JSON.stringify(data, null, 2) : data;
        if (!unsortedEntries.some((entry) => entry.key === newKey)) {
          unsortedEntries.push({ key: newKey, data: sanitizedData });
        }
      },
    );
    const rows = [
      commonEntries,
      unsortedEntries.sort((a, b) => a.key.localeCompare(b.key)),
    ].flat();

    return { ...testQuery, rows, description: test.description };
  }, [testQuery, commonEntries, uniqueId.packageName]);
};

export const TestDetailsTable: FC<ResourceDetailsParams> = ({
  className,
  children,
  compact,
  ...props
}) => {
  const testDetails = useTestDetailsPanel(props);
  return (
    <div className={className}>
      {children?.(testDetails)}
      <ColumnTable
        tableEntries={testDetails.rows}
        compact={compact}
        isLoading={testDetails.fetching}
      />
    </div>
  );
};
